import { createContext, useEffect, useMemo, useState } from "react";

import { DefaultGenerics, Event, StreamChat } from "stream-chat";

import { getUser } from "@/components/session/JwtTokenManager";
import { getBrowserTimezone } from "@/utils/datetime";

interface ChatContextType {
  unreadCount: number;
  unreadChannelCount?: number;
}

export const ChatContext = createContext<ChatContextType>({
  unreadCount: 0,
  unreadChannelCount: undefined,
});

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadChannelCount, setUnreadChannelCount] = useState<number>();

  function handleChatEvent(e: Event) {
    if (e.me) {
      setUnreadCount(e.me.total_unread_count);
      setUnreadChannelCount(e.me.unread_channels);
    }

    if (typeof e.total_unread_count !== "undefined") {
      setUnreadCount(e.total_unread_count);
    }

    if (typeof e.unread_channels !== "undefined") {
      setUnreadChannelCount(e.unread_channels);
    }
  }

  useEffect(() => {
    const chatClient = createChatInstance();
    loadChatContext(chatClient);
    chatClient.on(handleChatEvent);

    return () => {
      chatClient.off(handleChatEvent);
      chatClient.disconnectUser();
    };
  }, []);

  const value = useMemo(
    () => ({ unreadCount, unreadChannelCount }),
    [unreadCount, unreadChannelCount],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

function createChatInstance() {
  return new StreamChat(STREAM_CHAT_API_KEY, {
    timeout: 30000,
  });
}

export function getChatClient() {
  if (!internalChatClient) {
    internalChatClient = createChatInstance();
    loadChatContext(internalChatClient);
  }
  return internalChatClient;
}

export async function loadChatContext(instance: StreamChat<DefaultGenerics>) {
  const user = getUser();
  if (!user) return;

  const userId = user.id + "";
  return await instance.connectUser(
    {
      id: userId,
      name: user.name ?? undefined,
      image: user.photo,
      timezone: getBrowserTimezone(),
    },
    user.chatToken,
  );
}

let internalChatClient: StreamChat<DefaultGenerics> | null = null;
